<template>
  <component
    :is="componentToRender"
    v-bind="getAttributes()"
    :class="currentClass"
    @click="onClick"
    @onFocus="onFocus"
    @blur="onBlur"
  >
    <o-icon
      v-if="icon && !iconAfter"
      :icon="icon"
      :class="leftIconClass"
      fill
    />
    <div v-if="$slots.default || label" :class="{ 'opacity-0' : loading }" class="leading-none">
      <slot>
        {{ label }}
      </slot>
    </div>
    <o-icon
      v-if="icon && iconAfter"
      :icon="icon"
      :class="rightIconClass"
      fill
    />

    <div
      v-if="loading"
      class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
    >
      <svg aria-hidden="true" class="mx-auto h-4 w-4 animate-spin fill-primary-200 text-gray-200 dark:text-gray-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
      </svg>
    </div>

    <div v-if="badge" class="mt-1 flex items-center justify-center min-w-5 h-5 absolute rounded-xl bg-red-600 text-white border border-white shadow text-xs top-0 right-0 transform translate-x-2 -translate-y-2 leading-none text-center">
      <p class="px-1">
        {{ badge }}
      </p>
    </div>
  </component>
</template>

<script lang="ts">
export default { name: 'OButton' }
</script>

<script setup lang="ts">
interface Props {
  id?: string | null
  autofocus?: boolean | null
  disabled?: boolean
  name?: string | null
  label?: string | null
  badge?: string | number | null
  tabindex?: string | number | null
  value?: string | number | null
  type?: string
  href?: string | null
  size?: string | null
  icon?: string | string[] | null
  iconSize?: number
  iconAfter?: boolean
  method?: string
  data?: object
  preserveState?: boolean
  preserveScroll?: boolean
  to?: string | object
  replace?: boolean
  append?: boolean
  baseClass?: string
  hoverClass?: string
  activeClass?: string
  exact?: boolean
  exactActiveClass?: string
  rounded?: string
  translucent?: boolean
  transparent?: boolean
  hover?: boolean
  loading?: boolean
  dashed?: boolean
  target?: string | null
  variant?: string | null
  flat?: boolean
  outline?: boolean
  tagName?: string
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  autofocus: null,
  disabled: false,
  name: null,
  label: null,
  badge: null,
  tabindex: null,
  value: null,
  type: 'button',
  href: null,
  size: null,
  icon: null,
  iconSize: undefined,
  iconAfter: false,
  method: undefined,
  data: undefined,
  preserveState: false,
  preserveScroll: false,
  to: undefined,
  replace: false,
  append: false,
  baseClass: 'leading-loose block inline-flex items-center justify-center transition ease-in-out duration-300 transform cursor-pointer',
  hoverClass: 'hover:scale-102 hover:shadow',
  activeClass: 'router-link-active',
  exact: false,
  exactActiveClass: 'router-link-exact-active',
  rounded: 'rounded-lg',
  translucent: false,
  transparent: false,
  hover: true,
  loading: false,
  dashed: false,
  target: null,
  variant: null,
  flat: false,
  outline: false,
  tagName: 'a',
})

const emit = defineEmits<{
  (e: 'blur', event: Event): void
  (e: 'focus', event: Event): void
  (e: 'click', event: Event): void
}>()

// Colour classes
const defaultColorClass = 'text-gray-700 bg-gray-200 dark:bg-gray-800 dark:text-white/80 hover:bg-gray-300 dark:border-gray-900'
const infoColorClass = 'text-white bg-blue-600 dark:bg-blue-800 border-blue-400 hover:bg-blue-700'
const successColorClass = 'text-white bg-green-500 dark:bg-green-700 border-green-400 hover:bg-green-700'
const dangerColorClass = 'text-white bg-red-500 dark:bg-red-700 border-red-500 hover:bg-red-600 hover:border-red-600'
const warningColorClass = 'text-white bg-orange-500 dark:bg-orange-700 border-orange-500 hover:bg-orange-600 hover:border-orange-600'
const lightColorClass = 'text-white bg-gray-100 hover:bg-gray-200 hover:text-gray-700'
const darkColorClass = 'text-white bg-gray-700 hover:bg-gray-700'
const primaryColorClass = 'text-white bg-primary-600 dark:bg-primary-800 hover:bg-primary-600/85'
const secondaryColorClass = 'text-white bg-secondary hover:bg-secondary/85'

const defaultTranslucentColorClass = 'text-gray-700 bg-gray-500/10 hover:bg-gray-500/20 dark:text-white dark:bg-white/10 dark:hover:bg-white/20'
const infoTranslucentColorClass = 'text-blue-700 bg-blue-500/10 hover:bg-blue-500/20 dark:text-blue-300'
const successTranslucentColorClass = 'text-green-700 bg-green-500/10 hover:bg-green-500/20 dark:text-green-300'
const dangerTranslucentColorClass = 'text-red-700 bg-red-500/10 hover:bg-red-500/20 dark:text-red-300'
const warningTranslucentColorClass = 'text-orange-700 bg-orange-500/10 hover:bg-orange-500/20 dark:text-orange-300'
const lightTranslucentColorClass = 'text-white bg-white/15 hover:bg-white/25 hover:text-white'
const darkTranslucentColorClass = 'text-gray-900 bg-gray-900/10 hover:bg-gray-900/20 dark:text-white dark:bg-white/10 dark:hover:bg-white/20'
const primaryTranslucentColorClass = 'text-primary-700 bg-primary-500/10 hover:bg-primary-500/20 dark:text-primary-300'
const secondaryTranslucentColorClass = 'text-secondary bg-secondary/10 hover:bg-secondary/20'

const defaultFlatClass = 'text-gray-600 bg-gray-100 dark:bg-white/5 dark:text-white/80 hover:bg-gray-200 hover:text-gray-700'
const infoFlatClass = 'bg-blue-200 dark:bg-blue-800/20 text-blue-700 dark:text-blue-500 hover:bg-blue-600 hover:text-white'
const successFlatClass = 'bg-green-100 dark:bg-green-800/20 text-green-700 dark:text-green-500 hover:bg-green-600 hover:text-white'
const dangerFlatClass = 'bg-red-200 dark:bg-red-800/20 text-red-700 dark:text-red-500 hover:bg-red-600 hover:text-white'
const warningFlatClass = 'bg-orange-100 dark:bg-orange-800/20 text-orange-700 dark:text-orange-500 hover:bg-orange-600 hover:text-white'
const lightFlatClass = 'bg-gray-100 dark:bg-blue-800/20 text-gray-700 dark:text-gray-200 hover:bg-gray-200 hover:text-gray-700'
const darkFlatClass = 'bg-gray-100 dark:bg-blue-800/20 text-gray-700 dark:text-gray-200 hover:bg-gray-600 hover:text-white'
const primaryFlatClass = 'bg-primary-200 dark:bg-primary-800/20 text-primary-700 dark:text-primary-500 hover:bg-primary-600 hover:text-white'

const defaultOutlineClass = 'border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-white dark:text-gray-300'
const infoOutlineClass = 'border border-blue-400 dark:border-blue-800 text-blue-600'
const successOutlineClass = 'border border-green-400 dark:border-green-800 text-green-600'
const dangerOutlineClass = 'border border-red-400 dark:border-red-800 text-red-600'
const warningOutlineClass = 'border border-orange-400 dark:border-orange-800 text-orange-500'
const lightOutlineClass = 'border border-gray-50 dark:border-gray-50 text-gray-50'
const darkOutlineClass = 'border border-gray-800 dark:border-gray-800 text-gray-800'
const primaryOutlineClass = 'border border-primary-400 dark:border-primary-800 text-primary-600 dark:text-primary-400'

function variantsHelper(classes: string[]) {
  if (!props.flat && !props.outline) {
    if (props.translucent) {
      switch (props.variant) {
        case 'info': classes.push(infoTranslucentColorClass); break
        case 'danger': classes.push(dangerTranslucentColorClass); break
        case 'warning': classes.push(warningTranslucentColorClass); break
        case 'success': classes.push(successTranslucentColorClass); break
        case 'light': classes.push(lightTranslucentColorClass); break
        case 'dark': classes.push(darkTranslucentColorClass); break
        case 'primary': classes.push(primaryTranslucentColorClass); break
        case 'secondary': classes.push(secondaryTranslucentColorClass); break
        default: classes.push(defaultTranslucentColorClass); break
      }
      return classes
    }
    switch (props.variant) {
      case 'info': classes.push(infoColorClass); break
      case 'danger': classes.push(dangerColorClass); break
      case 'warning': classes.push(warningColorClass); break
      case 'success': classes.push(successColorClass); break
      case 'light': classes.push(lightColorClass); break
      case 'dark': classes.push(darkColorClass); break
      case 'primary': classes.push(primaryColorClass); break
      case 'secondary': classes.push(secondaryColorClass); break
      default: classes.push(defaultColorClass); break
    }
  }
  if (props.flat) {
    switch (props.variant) {
      case 'info': classes.push(infoFlatClass); break
      case 'danger': classes.push(dangerFlatClass); break
      case 'warning': classes.push(warningFlatClass); break
      case 'success': classes.push(successFlatClass); break
      case 'light': classes.push(lightFlatClass); break
      case 'dark': classes.push(darkFlatClass); break
      case 'primary': classes.push(primaryFlatClass); break
      default: classes.push(defaultFlatClass); break
    }
  }
  if (props.outline) {
    switch (props.variant) {
      case 'info': classes.push(infoOutlineClass); break
      case 'danger': classes.push(dangerOutlineClass); break
      case 'warning': classes.push(warningOutlineClass); break
      case 'success': classes.push(successOutlineClass); break
      case 'light': classes.push(lightOutlineClass); break
      case 'dark': classes.push(darkOutlineClass); break
      case 'primary': classes.push(primaryOutlineClass); break
      default: classes.push(defaultOutlineClass); break
    }
  }
  return classes
}

// Size classes
const disabledClass = 'opacity-50 pointer-events-none cursor-default'
const transparentClass = '/0'
const defaultSizeClass = 'px-3 h-8 text-sm'
const slimSizeClass = 'px-2 h-8 text-sm'
const largeSizeClass = 'px-4 h-10 text-base'
const smallSizeClass = 'px-2 h-7 py-1 text-sm'
const extraSmallSizeClass = 'px-2 h-6 text-sm'

// Type mixin (componentToRender)
const isARouterLink = computed(() => props.to !== undefined)
const isAnIntertiaLink = computed(() => props.href !== undefined && false) // InertiaLink not available

const componentToRender = computed(() => {
  if (isARouterLink.value) {
    return resolveComponent('NuxtLink')
  }
  if (props.href) {
    return 'a'
  }
  return props.tagName
})

const isSmall = computed(() => ['xs', 'sm'].includes(props.size as string))

const currentClass = computed(() => {
  const classes = [
    'o-button',
    props.baseClass,
    props.rounded,
  ]
  if (props.disabled || props.loading) {
    classes.push(disabledClass)
  }
  if (props.transparent) {
    classes.push(transparentClass)
  }
  if (props.hover) {
    classes.push(props.hoverClass)
  }
  if (props.dashed) {
    classes.push('border-dashed')
  }
  if (props.size === null) {
    classes.push(defaultSizeClass)
  } else if (props.size === 'sm') {
    classes.push(smallSizeClass)
  } else if (props.size === 'lg') {
    classes.push(largeSizeClass)
  } else if (props.size === 'xs') {
    classes.push(extraSmallSizeClass)
  } else if (props.size === 'slim') {
    classes.push(slimSizeClass)
  }

  const slots = useSlots()
  if (!slots.default && !props.label) {
    if (props.size === null) {
      classes.push('w-8')
    } else if (props.size === 'sm') {
      classes.push('w-7')
    } else if (props.size === 'lg') {
      classes.push('w-10')
    } else if (props.size === 'xs') {
      classes.push('w-6')
    } else if (props.size === 'slim') {
      classes.push('w-8')
    }
  }

  return variantsHelper(classes)
})

const slots = useSlots()

const leftIconClass = computed(() => {
  const classes: string[] = ['flex-shrink-0']
  if (slots.default || props.label) {
    classes.push(isSmall.value ? 'mr-2' : 'mr-3')
    classes.push('hidden md:block')
  }
  if (props.loading) {
    classes.push('opacity-0')
  }
  return classes
})

const rightIconClass = computed(() => {
  const classes: string[] = ['flex-shrink-0']
  if (slots.default) {
    classes.push(isSmall.value ? 'ml-2' : 'ml-3')
    classes.push('hidden md:block')
  }
  if (props.loading) {
    classes.push('opacity-0')
  }
  return classes
})

function onBlur(e: Event) {
  emit('blur', e)
}

function onFocus(e: Event) {
  emit('focus', e)
}

function onClick(e: Event) {
  emit('click', e)
}

function getAttributes() {
  if (isAnIntertiaLink.value) {
    return {
      href: props.href,
      method: props.method,
      data: props.data,
      preserveState: props.preserveState,
      preserveScroll: props.preserveScroll,
      id: props.id,
      value: props.value,
      autofocus: props.autofocus,
      disabled: props.disabled || props.loading,
      name: props.name,
      type: props.type,
    }
  }
  if (isARouterLink.value) {
    return {
      to: props.to,
      replace: props.replace,
      append: props.append,
      tag: props.tagName,
      activeClass: props.activeClass,
      exact: props.exact,
      event: ['click', 'focus', 'blur'],
      exactActiveClass: props.exactActiveClass,
      id: props.id,
      value: props.value,
      autofocus: props.autofocus,
      disabled: props.disabled || props.loading,
      name: props.name,
      type: props.type,
      target: props.target,
    }
  }
  return {
    id: props.id,
    value: props.value,
    autofocus: props.autofocus,
    disabled: props.disabled || props.loading,
    name: props.name,
    href: props.href,
    type: props.type,
    target: props.target,
  }
}
</script>
